import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Search.css';

// Normalize strings: lowercase, trim, remove accents
const normalize = (str = '') =>
  str
    .toString()
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

// Champion aliases for easier searching
const championAliases = {
  'mf': 'miss fortune',
};

const Search = ({ guessHandler, searchOptions = [] }) => {
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const normalizedQuery = normalize(query);
  const filteredOptions = useMemo(() => {
    if (!normalizedQuery) return searchOptions;
    return searchOptions.filter(opt => {
      const val = normalize(opt.value);
      const label = normalize(opt.label);
      
      // Check if query matches the value or label directly
      if (val.startsWith(normalizedQuery) || label.startsWith(normalizedQuery)) {
        return true;
      }
      
      // Check if query matches any aliases for this champion
      const aliasedChampion = championAliases[normalizedQuery];
      if (aliasedChampion && val === normalize(aliasedChampion)) {
        return true;
      }
      
      return false;
    });
  }, [searchOptions, normalizedQuery]);

  const commitGuess = (option) => {
    if (!option) return;
    try { guessHandler(option.value); } catch (_) {}
    setQuery('');
    setIsOpen(false);
    setActiveIndex(-1);
    // Refocus after state updates
    setTimeout(() => {
      try { inputRef.current && inputRef.current.focus && inputRef.current.focus(); } catch (_) {}
    }, 0);
  };

  const onKeyDown = (e) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setIsOpen(true);
      setActiveIndex(0);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = filteredOptions[activeIndex] || filteredOptions[0];
      commitGuess(option);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const onChange = (e) => {
    setQuery(e.target.value);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const onOptionClick = (idx) => {
    const option = filteredOptions[idx];
    commitGuess(option);
  };

  // Keep input focused when options change after a guess
  useEffect(() => {
    try { inputRef.current && inputRef.current.focus && inputRef.current.focus(); } catch (_) {}
  }, [searchOptions]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div className="search-container" ref={containerRef}>
      <input
        ref={inputRef}
        className="search-input"
        type="text"
        value={query}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder="Type a champion..."
        autoFocus
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls="search-options-listbox"
        role="combobox"
      />
      {isOpen && filteredOptions.length > 0 && (
        <ul
          id="search-options-listbox"
          className="search-options"
          role="listbox"
        >
          {filteredOptions.map((opt, idx) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={idx === activeIndex}
              className={`search-option ${idx === activeIndex ? 'active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onOptionClick(idx)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Search;
